import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  ScanLine, Truck, ShieldCheck, Star, Phone, MapPin, Clock,
  ArrowRight, Quote, Search, FileText, MessageSquare, CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useAuth } from "@/context/AuthContext";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08 } }),
};

const reviews = [
  { name: "Hakob Tadevosyan", text: "Le meilleur fournisseur de Asse ! Les pièces sont toujours de grande qualité et la livraison extrêmement rapide.", stars: 5 },
  { name: "Khedim Rizlène", text: "Je viens de France et ma voiture était en panne. Stukjes a pu fournir une pièce très rapidement et à un prix raisonnable, merci !", stars: 5 },
  { name: "Christopher De Neef", text: "Personnel serviable et bons services. Je recommande pour toutes vos pièces auto.", stars: 5 },
];

export default function Landing() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const ctaTo = user ? "/app" : "/register";
  const VIN_LABEL = { fr: "VIN détecté", nl: "VIN gedetecteerd", en: "VIN detected", ro: "VIN detectat", pl: "Wykryto VIN", pt: "VIN detetado", tr: "VIN algılandı" };

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Nav */}
      <header className="sticky top-0 z-50 border-b border-slate-100 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3.5 lg:px-8">
          <Logo />
          <nav className="hidden items-center gap-8 text-sm font-medium text-slate-600 md:flex">
            <a href="#process" className="hover:text-blue-600 transition-colors">{t("nav.howItWorks")}</a>
            <a href="#avis" className="hover:text-blue-600 transition-colors">{t("nav.reviews")}</a>
            <a href="#contact" className="hover:text-blue-600 transition-colors">{t("nav.contact")}</a>
          </nav>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <Link to="/login" data-testid="nav-login-link">
              <Button variant="ghost" className="text-slate-700">{t("nav.login")}</Button>
            </Link>
            <Link to={ctaTo} data-testid="nav-cta-link">
              <Button className="bg-blue-600 hover:bg-blue-700">{t("nav.clientArea")}</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(60%_60%_at_70%_0%,#DBEAFE_0%,transparent_60%)]" />
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 py-20 lg:grid-cols-2 lg:px-8 lg:py-28">
          <motion.div variants={fadeUp} initial="hidden" animate="show">
            <span className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
              <Star className="h-3.5 w-3.5 fill-blue-600 text-blue-600" /> {t("hero.badge")}
            </span>
            <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              {t("hero.titleL1")}<br />
              <span className="text-blue-600">{t("hero.titleHl")}</span>
            </h1>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-slate-600">
              {t("hero.subtitle")}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to={ctaTo} data-testid="hero-cta-btn">
                <Button size="lg" className="bg-blue-600 px-6 hover:bg-blue-700">
                  {t("hero.cta")} <ArrowRight className="ml-1.5 h-4 w-4" />
                </Button>
              </Link>
              <a href="#process">
                <Button size="lg" variant="outline" className="border-slate-300">{t("nav.howItWorks")}</Button>
              </a>
            </div>
            <div className="mt-10 flex flex-wrap gap-6 text-sm text-slate-500">
              <span className="flex items-center gap-2"><Truck className="h-4 w-4 text-blue-600" /> {t("hero.fast")}</span>
              <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-blue-600" /> {t("hero.quality")}</span>
              <span className="flex items-center gap-2"><ScanLine className="h-4 w-4 text-blue-600" /> {t("hero.ai")}</span>
            </div>
          </motion.div>

          <motion.div variants={fadeUp} custom={1} initial="hidden" animate="show" className="relative">
            <div className="overflow-hidden rounded-2xl border border-slate-200 shadow-2xl shadow-blue-900/10">
              <img
                src="/hero-parts.png"
                alt="Pièces auto neuves"
                className="h-[420px] w-full bg-slate-50 object-contain p-4"
              />
            </div>
            <div className="absolute -bottom-5 -left-5 hidden rounded-xl border border-slate-200 bg-white p-4 shadow-xl sm:block">
              <div className="flex items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-lg bg-blue-600 text-white"><ScanLine className="h-5 w-5" /></span>
                <div>
                  <p className="text-xs text-slate-500">{VIN_LABEL[i18n.language] || VIN_LABEL.en}</p>
                  <p className="font-mono text-sm font-semibold text-slate-900">WVWZZZ1KZAW***</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Process */}
      <section id="process" className="border-y border-slate-100 bg-slate-50 py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-600">{t("process.tag")}</p>
            <h2 className="mt-2 font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("process.heading")}</h2>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {[
              { icon: FileText, t: t("process.s1t"), d: t("process.s1d") },
              { icon: Search, t: t("process.s2t"), d: t("process.s2d") },
              { icon: MessageSquare, t: t("process.s3t"), d: t("process.s3d") },
            ].map((s, i) => (
              <motion.div
                key={s.t}
                variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }}
                className="group rounded-xl border border-slate-200 bg-white p-7 transition-all duration-200 hover:-translate-y-1 hover:shadow-lg"
              >
                <span className="grid h-12 w-12 place-items-center rounded-xl bg-blue-50 text-blue-600 transition-colors group-hover:bg-blue-600 group-hover:text-white">
                  <s.icon className="h-6 w-6" />
                </span>
                <h3 className="mt-5 font-display text-lg font-semibold">{s.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section id="avis" className="py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-600">{t("testi.tag")}</p>
              <h2 className="mt-2 font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("testi.heading")}</h2>
            </div>
            <div className="flex items-center gap-1 text-amber-500">
              {[...Array(5)].map((_, i) => <Star key={i} className="h-5 w-5 fill-amber-400 text-amber-400" />)}
              <span className="ml-2 text-sm font-semibold text-slate-700">{t("testi.google")}</span>
            </div>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {reviews.map((r, i) => (
              <motion.figure
                key={r.name}
                variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }}
                className="rounded-xl border border-slate-200 bg-white p-7 shadow-sm"
              >
                <Quote className="h-7 w-7 text-blue-200" />
                <blockquote className="mt-3 text-sm leading-relaxed text-slate-700">"{r.text}"</blockquote>
                <figcaption className="mt-5 flex items-center gap-3">
                  <span className="grid h-9 w-9 place-items-center rounded-full bg-blue-600 text-sm font-semibold text-white">
                    {r.name.charAt(0)}
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{r.name}</p>
                    <div className="flex">{[...Array(r.stars)].map((_, k) => <Star key={k} className="h-3 w-3 fill-amber-400 text-amber-400" />)}</div>
                  </div>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      {/* Contact / CTA */}
      <section id="contact" className="bg-slate-900 py-20 text-white sm:py-24">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-2 lg:px-8">
          <div>
            <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("contact.heading")}</h2>
            <p className="mt-4 max-w-md text-slate-300">{t("contact.subtitle")}</p>
            <div className="mt-8 space-y-4 text-sm">
              <p className="flex items-center gap-3"><MapPin className="h-5 w-5 text-blue-400" /> Gentsesteenweg 1, 1730 Asse, Belgique</p>
              <p className="flex items-center gap-3"><Phone className="h-5 w-5 text-blue-400" /> 02 449 73 17</p>
              <p className="flex items-center gap-3"><Clock className="h-5 w-5 text-blue-400" /> {t("contact.hours")}</p>
            </div>
            <Link to={ctaTo} className="mt-8 inline-block" data-testid="contact-cta-btn">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700">{t("contact.cta")}</Button>
            </Link>
          </div>
          <div className="grid gap-4">
            {[t("contact.f1"), t("contact.f2"), t("contact.f3"), t("contact.f4")].map((f) => (
              <div key={f} className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-5 py-4">
                <CheckCircle2 className="h-5 w-5 shrink-0 text-blue-400" />
                <span className="text-sm font-medium">{f}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-800 bg-slate-900 py-8 text-center text-sm text-slate-400">
        <p>© {new Date().getFullYear()} Stukjes Spare Parts · Gentsesteenweg 1, 1730 Asse</p>
      </footer>
    </div>
  );
}
